/**
 * TeamDeleteDialog: the one confirm dialog for deleting a team, for the
 * owner (Settings > Teams) and for staff (the team's config pages). It
 * shows what the delete would remove (the preview the caller fetched),
 * why it would refuse, and asks for the team's name. The caller owns the
 * mutation and the preview query, as ConfirmDialog asks.
 */

import { useState } from 'react'

import type { TeamDeletePreview } from '@/api/generated/model'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

import { CONVERSATIONS_GO, canConfirmDelete, previewLines } from './teamDeletePreview'

export interface TeamDeletePreviewState {
  data: TeamDeletePreview | undefined
  isLoading: boolean
  /** Null while the preview loads or loaded. */
  error: string | null
}

interface TeamDeleteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The team's display name, which the user types to confirm. */
  teamName: string
  preview: TeamDeletePreviewState
  isPending: boolean
  /** A failed delete's message. */
  error: string | null
  onConfirm: (confirm: string) => void
}

export function TeamDeleteDialog({
  open,
  onOpenChange,
  teamName,
  preview,
  isPending,
  error,
  onConfirm,
}: TeamDeleteDialogProps): React.JSX.Element {
  const [typed, setTyped] = useState('')
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setTyped('')
        onOpenChange(next)
      }}
      title={`Delete ${teamName}`}
      description="This deletes everything the team owns. It cannot be undone."
      confirmLabel="Delete team"
      pendingLabel="Deleting..."
      destructive
      isPending={isPending}
      confirmDisabled={!canConfirmDelete(preview.data, typed, teamName)}
      error={error}
      onConfirm={() => {
        onConfirm(typed)
      }}
    >
      <TeamDeleteDialogBody preview={preview} />
      <div className="space-y-2">
        <Label htmlFor="delete-confirm">
          Type <strong>{teamName}</strong> to confirm
        </Label>
        <Input
          id="delete-confirm"
          value={typed}
          onChange={(e) => {
            setTyped(e.target.value)
          }}
          placeholder={teamName}
          autoComplete="off"
        />
      </div>
    </ConfirmDialog>
  )
}

/** The preview as the dialog lists it; exported so the list can be tested without the portal. */
export function TeamDeleteDialogBody({
  preview,
}: {
  preview: TeamDeletePreviewState
}): React.JSX.Element {
  if (preview.isLoading) return <Skeleton className="h-16 w-full" />
  if (preview.error !== null) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {preview.error}
      </p>
    )
  }
  if (preview.data === undefined) return <></>
  const lines = previewLines(preview.data)
  return (
    <div className="space-y-3 text-sm">
      <dl className="space-y-1">
        {lines.map((line) => (
          <div key={line.label} className="flex gap-2">
            <dt className="shrink-0 text-muted-foreground">{line.label}</dt>
            <dd className="min-w-0 break-words text-foreground">{line.detail}</dd>
          </div>
        ))}
      </dl>
      <p className="text-xs text-muted-foreground">{CONVERSATIONS_GO}</p>
      {preview.data.conversations_note !== null && (
        <p className="text-xs text-muted-foreground">{preview.data.conversations_note}</p>
      )}
      {preview.data.skipped.map((note) => (
        <p key={note} className="text-xs text-muted-foreground">
          {note}
        </p>
      ))}
      {preview.data.blockers.length > 0 && (
        <ul className="space-y-1" role="alert">
          {preview.data.blockers.map((blocker) => (
            <li key={blocker} className="text-destructive">
              {blocker}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
